import React, { useEffect, useState } from 'react';
import { getFirestore, collection, query, where, orderBy, limit, startAfter, endBefore, limitToLast, getDocs } from 'firebase/firestore';
import { app } from './Firebase';
import logo from './images/mascot.png';
import { getResultPathForDocument } from './resultPaths.js';

const PhotosListPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [firstDoc, setFirstDoc] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasNext, setHasNext] = useState(true);
  const [hasPrev, setHasPrev] = useState(false);
  const [page, setPage] = useState(1);
  const [targetPage, setTargetPage] = useState('');
  const [isJumping, setIsJumping] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedDocs, setExpandedDocs] = useState(new Set());

  const DOCS_PER_PAGE = 10;

  const loadDocuments = async (direction = 'initial') => {
    setLoading(true);
    setError(null);

    try {
      const db = getFirestore(app);
      let q;

      if (direction === 'next' && lastDoc) {
        q = query(
          collection(db, "petPhotos"),
          orderBy("timestamp", "asc"),
          startAfter(lastDoc),
          limit(DOCS_PER_PAGE)
        );
      } else if (direction === 'prev' && firstDoc) {
        q = query(
          collection(db, "petPhotos"),
          orderBy("timestamp", "asc"),
          endBefore(firstDoc),
          limitToLast(DOCS_PER_PAGE)
        );
      } else {
        // Initial load
        q = query(
          collection(db, "petPhotos"),
          orderBy("timestamp", "asc"),
          limit(DOCS_PER_PAGE)
        );
      }

      const querySnapshot = await getDocs(q);
      const newDocs = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const photos = data.photos?.['YourPet'] || data.photos?.['Your Pet'] || [];
        
        // Skip documents that have image_quality field OR no photos
        if (!data.image_quality && photos.length > 0) {
          newDocs.push({
            id: doc.id,
            ...data
          });
        }
      });
      
      // Always replace documents for page-based navigation
      setDocuments(newDocs);

      // Set first and last documents for pagination
      if (querySnapshot.docs.length > 0) {
        setFirstDoc(querySnapshot.docs[0]);
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }

      // Update page counter
      if (direction === 'next') {
        setPage(prev => prev + 1);
        setHasPrev(true);
      } else if (direction === 'prev') {
        setPage(prev => prev - 1);
        setHasPrev(page > 2);
      } else {
        setPage(1);
        setHasPrev(false);
      }

      // Check if there are more documents for next page
      setHasNext(querySnapshot.docs.length === DOCS_PER_PAGE);

    } catch (err) {
      console.error("Error fetching documents:", err);
      setError("Error fetching documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const handleImageDoubleClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const toggleExpandDoc = (docId) => {
    setExpandedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  // Function to get a smaller/faster loading version of the image
  const getThumbnailUrl = (originalUrl) => {
    if (!originalUrl) return originalUrl;

    // Try common Firebase Storage thumbnail patterns
    try {
      // Pattern 1: Look for resized versions (if Firebase Image Resize extension is used)
      if (originalUrl.includes('firebasestorage.googleapis.com')) {
        // Try adding common thumbnail suffixes
        const url = new URL(originalUrl);
        const pathParts = url.pathname.split('.');
        if (pathParts.length >= 2) {
          const extension = pathParts.pop();
          const basePath = pathParts.join('.');
          
          // Try different thumbnail naming patterns
          const thumbnailPatterns = [
            `${basePath}_300x300.${extension}`,
            `${basePath}_thumb.${extension}`,
            `${basePath}_small.${extension}`,
            `${basePath}_200x200.${extension}`
          ];
          
          // For now, let's try the first pattern and fallback to original
          url.pathname = thumbnailPatterns[0];
          return url.toString();
        }
      }
    } catch (error) {
      console.log('Error creating thumbnail URL:', error);
    }

    // Fallback to original URL
    return originalUrl;
  };

  // Preload images for better performance
  useEffect(() => {
    if (documents.length > 0) {
      documents.forEach(doc => {
        const photos = doc.photos?.['YourPet'] || doc.photos?.['Your Pet'] || [];
        // Preload only first 3 images of each document with thumbnail URLs
        photos.slice(0, 3).forEach(photoUrl => {
          const img = new Image();
          img.src = getThumbnailUrl(photoUrl);
          // Also preload original as fallback
          img.onerror = () => {
            const originalImg = new Image();
            originalImg.src = photoUrl;
          };
        });
      });
    }
  }, [documents]);

  const handleNextPage = () => {
    if (!loading && hasNext) {
      loadDocuments('next');
    }
  };

  const handlePrevPage = () => {
    if (!loading && hasPrev) {
      loadDocuments('prev');
    }
  };

  const jumpToPage = async (pageNumber) => {
    if (pageNumber < 1 || isJumping || loading) return;
    
    setIsJumping(true);
    setLoading(true);
    setError(null);

    try {
      const db = getFirestore(app);
      let currentPage = 1;
      let lastDocForPage = null;
      
      // If jumping to page 1, just load initial
      if (pageNumber === 1) {
        await loadDocuments();
        setIsJumping(false);
        return;
      }

      // Fetch documents page by page until we reach the target page
      while (currentPage < pageNumber) {
        let q;
        if (lastDocForPage) {
          q = query(
            collection(db, "petPhotos"),
            orderBy("timestamp", "asc"),
            startAfter(lastDocForPage),
            limit(DOCS_PER_PAGE)
          );
        } else {
          q = query(
            collection(db, "petPhotos"),
            orderBy("timestamp", "asc"),
            limit(DOCS_PER_PAGE)
          );
        }

        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.docs.length === 0) {
          // No more documents, we've gone too far
          setError(`Page ${pageNumber} does not exist. Maximum page reached.`);
          setIsJumping(false);
          setLoading(false);
          return;
        }

        lastDocForPage = querySnapshot.docs[querySnapshot.docs.length - 1];
        currentPage++;
      }

      // Now fetch the actual target page
      let q;
      if (lastDocForPage) {
        q = query(
          collection(db, "petPhotos"),
          orderBy("timestamp", "asc"),
          startAfter(lastDocForPage),
          limit(DOCS_PER_PAGE)
        );
      } else {
        q = query(
          collection(db, "petPhotos"),
          orderBy("timestamp", "asc"),
          limit(DOCS_PER_PAGE)
        );
      }

      const querySnapshot = await getDocs(q);
      const newDocs = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const photos = data.photos?.['YourPet'] || data.photos?.['Your Pet'] || [];
        
        if (!data.image_quality && photos.length > 0) {
          newDocs.push({
            id: doc.id,
            ...data
          });
        }
      });

      setDocuments(newDocs);
      setPage(pageNumber);
      setHasPrev(pageNumber > 1);
      setHasNext(querySnapshot.docs.length === DOCS_PER_PAGE);

      if (querySnapshot.docs.length > 0) {
        setFirstDoc(querySnapshot.docs[0]);
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }

    } catch (err) {
      console.error("Error jumping to page:", err);
      setError("Error jumping to page");
    } finally {
      setIsJumping(false);
      setLoading(false);
    }
  };

  const handleJumpToPage = () => {
    const pageNum = parseInt(targetPage);
    if (pageNum && pageNum > 0) {
      jumpToPage(pageNum);
      setTargetPage('');
    }
  };

  const renderPhotos = (doc) => {
    const photos = doc.photos?.['YourPet'] || doc.photos?.['Your Pet'] || [];
    
    if (photos.length === 0) {
      return <div className="text-gray-500 text-sm">No photos available</div>;
    }

    const isExpanded = expandedDocs.has(doc.id);
    const photosToShow = isExpanded ? photos : photos.slice(0, 3);
    const hasMore = photos.length > 3;

    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {photosToShow.map((photoUrl, index) => (
            <div key={index} className="relative bg-gray-200 rounded-lg overflow-hidden cursor-pointer" style={{ aspectRatio: '9/16' }}>
              <img 
                src={getThumbnailUrl(photoUrl)}
                alt={`Pet photo ${index + 1}`}
                loading="lazy"
                className="w-full h-full object-cover rounded-lg shadow-sm opacity-0 transition-opacity duration-300 hover:scale-105 transition-transform"
                onDoubleClick={() => handleImageDoubleClick(photoUrl)}
                onLoad={(e) => {
                  e.target.style.opacity = '1';
                  // Hide loading spinner
                  const spinner = e.target.nextElementSibling;
                  if (spinner) spinner.style.display = 'none';
                }}
                onError={(e) => {
                  // If thumbnail fails, try original URL
                  if (e.target.src !== photoUrl) {
                    e.target.src = photoUrl;
                  } else {
                    e.target.style.display = 'none';
                    // Hide loading spinner
                    const spinner = e.target.nextElementSibling;
                    if (spinner) spinner.style.display = 'none';
                  }
                }}
              />
              {/* Loading placeholder */}
              <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gray-400 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
              {/* Double-click hint */}
              <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Double-click to enlarge
              </div>
            </div>
          ))}
        </div>
        
        {/* Load More / Show Less Button */}
        {hasMore && (
          <div className="mt-3 text-center">
            <button
              onClick={() => toggleExpandDoc(doc.id)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              {isExpanded ? `Show Less (${photos.length - 3} hidden)` : `Load ${photos.length - 3} More Photos`}
            </button>
          </div>
        )}
      </div>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'No timestamp';
    
    try {
      // Handle Firestore timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (err) {
      return 'Invalid timestamp';
    }
  };

  return (
    <div className="container mx-auto p-4">
      <nav className="flex justify-between items-center p-4 mb-6">
        <div className="flex items-center">
          <img src={logo} alt="Fluffball.app Logo" className="h-10 mr-2" />
          <span className="text-xl font-semibold">fluffball</span>
        </div>
        <div className="flex gap-4">
          <a href="/" className="px-4 py-2 hover:bg-gray-100 rounded-md transition-colors">
            Home
          </a>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Pet Photos Collection</h1>
        <p className="text-gray-600 mb-8">
          Viewing documents with photos, without image_quality field, ordered by timestamp ascending
        </p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="space-y-8">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    Document ID: {doc.id}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Timestamp: {formatTimestamp(doc.timestamp)}
                  </p>
                  {doc.petType && (
                    <p className="text-sm text-gray-600">
                      Pet Type: {doc.petType}
                    </p>
                  )}
                </div>
                <a 
                  href={getResultPathForDocument(doc.id, doc)}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                >
                  View Result
                </a>
              </div>
              
              <div className="mt-4">
                {renderPhotos(doc)}
              </div>
            </div>
          ))}
        </div>

        {documents.length === 0 && !loading && (
          <div className="text-center text-gray-500 py-12">
            No documents found matching the criteria.
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        )}

        {/* Pagination Controls */}
        {documents.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
            <button
              onClick={handlePrevPage}
              disabled={!hasPrev || loading || isJumping}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                hasPrev && !loading && !isJumping
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              ← Previous Page
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="text-center">
                <span className="text-gray-600">Page {page}</span>
                <div className="text-sm text-gray-500 mt-1">
                  Showing {documents.length} documents
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label htmlFor="page-input" className="text-sm text-gray-600">
                  Go to page:
                </label>
                <input
                  id="page-input"
                  type="number"
                  min="1"
                  value={targetPage}
                  onChange={(e) => setTargetPage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleJumpToPage()}
                  disabled={loading || isJumping}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="Page"
                />
                <button
                  onClick={handleJumpToPage}
                  disabled={!targetPage || loading || isJumping}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    targetPage && !loading && !isJumping
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isJumping ? 'Jumping...' : 'Go'}
                </button>
              </div>
            </div>

            <button
              onClick={handleNextPage}
              disabled={!hasNext || loading || isJumping}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                hasNext && !loading && !isJumping
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next Page →
            </button>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white text-2xl hover:text-gray-300 transition-colors z-10"
              aria-label="Close modal"
            >
              ✕
            </button>
            <img
              src={selectedImage}
              alt="Full size image"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              style={{ maxHeight: 'calc(100vh - 80px)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotosListPage; 
